// Inert tool the tenant-profiling playbook calls as its FINAL step.
// After sampling the tenant's data and characterizing what the org does
// with AI, the agent calls this once with 6 tailored starter cards + a
// short profile. It writes them to the starterCardsStore, which rotates
// the empty-chat starter suggestions to the tailored set (persisted per
// tenant). Like suggest_kernel, it runs no analysis itself — it's just
// the structured hand-off from the run to the UI.

import { Type } from "@earendil-works/pi-ai";
import {
    browserStorageOwnerIsCurrent,
    currentBrowserStorageOwner,
} from "./browserStorageScope.js";
import { setStarterCardsForOwner } from "./starterCardsStore.js";

export function createSetStarterCardsTool(tenantId) {
    const owner = currentBrowserStorageOwner(tenantId);
    return {
        name: "set_starter_cards",
        label: "Set starter cards",
        description:
            "FINAL step of the tenant-profiling playbook ONLY. After you've sampled the tenant's data and characterized what the org does with AI, call this exactly once with a short `profile` and the 6 tailored starter cards. They render in the Tenant profile view, and (when the tailored-starters option is enabled) replace the default empty-chat starter suggestions. This is NOT a render or analysis tool — never call it during ordinary questions.",
        parameters: Type.Object({
            profile: Type.String({
                description:
                    "2-3 sentence characterization of what this org actually does with AI. Shown above the tailored cards.",
            }),
            cards: Type.Array(
                Type.Object({
                    title: Type.String({ description: "Short card title, 3-6 words." }),
                    body: Type.String({
                        description:
                            "The full prompt the card runs when clicked — a specific, ready-to-ask question grounded in THIS tenant's real themes and endpoint patterns.",
                    }),
                }),
                { description: "Exactly 6 tailored starter cards.", minItems: 1, maxItems: 8 },
            ),
        }),
        async execute(_id, args) {
            const ok = setStarterCardsForOwner(owner, {
                cards: args?.cards,
                profile: args?.profile,
            });
            if (!ok) {
                const message = !owner
                    ? "set_starter_cards needs a verified dashboard identity."
                    : !browserStorageOwnerIsCurrent(owner)
                        ? "set_starter_cards was refused because the dashboard identity changed."
                        : "set_starter_cards needs a non-empty `cards` array of { title, body } objects.";
                return {
                    content: [{ type: "text", text: message }],
                    details: { error: message },
                    isError: true,
                };
            }
            const count = args.cards.length;
            return {
                content: [
                    {
                        type: "text",
                        text: `Saved ${count} tailored starter cards — shown in the Tenant profile view.`,
                    },
                ],
                details: { kind: "starterCards", count, profile: args.profile },
            };
        },
    };
}
